import { UNKNOWN, USD } from './constants';
import { SubscriptionPlanName } from './enums';
import moment from 'moment';

const { BASIC, PREMIUM } = SubscriptionPlanName;

export function getProductPriceDetails(plan, isSubscriptionActive) {
  const priceAmount = plan?.unit_amount ?? 0;
  const priceCurrency = plan?.currency ?? USD;
  const isRecurring = plan?.recurring;

  const product = plan?.product;

  // Map price nickname to human-readable name using a lookup table
  const priceName = product?.name ?? '';
  const priceId = plan?.id ?? 'unknown';
  const status = plan?.status ?? 'unknown';
  // Calculate formatted price with currency handling
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: priceCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(priceAmount / 100);
  const lookupKey = plan?.lookup_key ?? UNKNOWN;
  return {
    price: formattedPrice,
    currency: priceCurrency,
    priceName,
    priceId,
    status,
    isRecurring,
    priceAmount,
    isFree: priceAmount == 0 && !isSubscriptionActive,
    productMetadata: product?.metadata,
    lookupKey,
  };
}
// Function to get the features
export const getFeatures = ({ priceName, featureCount, intl }) => {
  let features = [];
  for (let i = 0; i < featureCount; i++) {
    const feature = intl.formatMessage({
      id: `SubscriptionPage.${priceName}Features.${i}`,
      defaultMessage: '',
    });
    if (feature) features.push(feature);
  }
  return features;
};
// Known feature counts for each plan
export const featureCounts = {
  Basic: 5,
  Premium: 5,
};

export const formatDate = unixTimestamp => {
  if (!unixTimestamp) return null;

  return moment.unix(unixTimestamp).format('D MMMM');
};

const createFeaturePaths = (planPrefix, count) =>
  Array.from(
    { length: count },
    (_, i) => `SubscriptionPage.${String(planPrefix)?.toLowerCase()}.Feature${i}`
  );

const FEATURES_COUNT = {
  TALENT_STANDARD: 5,
  TALENT_PLUS: 3,
  NIL_JOB_POSTING: 1,
  NIL_DIY: 3,
  NIL_ASSIST: 3,
  NIL_COMPLETE: 3,
  NIL_PREMIER: 5,
  SCHOOL_STANDARD: 2,
  SCHOOL_PLUS: 4,
  AGENCY_STANDARD: 5,
  AGENCY_PLUS: 2,
  BRAND_STANDARD: 7,
  BRAND_PREMIER: 6,
};

// Build the SUBSCRIPTION_FEATURES object
export const SUBSCRIPTION_FEATURES = Object.entries(FEATURES_COUNT).reduce(
  (acc, [planKey, featureCount]) => {
    const subscriptionName = SubscriptionPlanName[planKey];
    if (!subscriptionName) {
      return acc; // Skip if no matching subscription name
    }

    acc[subscriptionName] = createFeaturePaths(planKey, featureCount);
    return acc;
  },
  {}
);

export const showQuoteForm = type => {
  return [
    SubscriptionPlanName.NIL_ASSIST,
    SubscriptionPlanName.NIL_DIY,
    SubscriptionPlanName.NIL_JOB_POSTING,
    SubscriptionPlanName.NIL_COMPLETE,
  ].includes(type);
};

export const createTitleGetter = titles => {
  const getTitle = titlesObj => {
    const keys = Object.keys(titlesObj);
    for (const key of keys) {
      if (titles[key]) {
        return titlesObj[key];
      }
    }
    // Return first title as fallback (prioritize order in object)
    return titlesObj[keys[0]];
  };

  return getTitle;
};

// ============================================
// Helper function to check if cancellation date is in future
// ============================================
export const isCancellationDateInFuture = cancellationUnix => {
  if (!cancellationUnix) return false;
  const cancellationDate = new Date(cancellationUnix * 1000);
  return cancellationDate > new Date();
};

// ============================================
// Helper function to check if user can cancel subscription
// ============================================
export const canUserCancelSubscription = ({
  isAuthenticated,
  isListingPage,
  isSubscriptionActive,
  isOwnListing,
  userPrice,
  planPrice,
  isPendingCancellation,
  cancellationDate,
}) => {
  return (
    isAuthenticated &&
    isListingPage &&
    isSubscriptionActive &&
    !isOwnListing &&
    userPrice === planPrice &&
    !isPendingCancellation &&
    !cancellationDate
  );
};
